import React from 'react'
import ComplianceHelp from './ComplianceHelp'
import { Link, useLocation } from 'react-router-dom'
import trainingVideoUrl from '../video/Maintain IQ training..mp4'

export default function Help() {
  const [searchTerm, setSearchTerm] = React.useState('')
  const [lastMatch, setLastMatch] = React.useState('')
  const [trainingOpen, setTrainingOpen] = React.useState(false)
  const [clipOpen, setClipOpen] = React.useState(false)
  const [clipConfig, setClipConfig] = React.useState(null)

  // Section refs for scrolling
  const quickLinksRef = React.useRef(null)
  const complianceRef = React.useRef(null)
  const helpLocation = useLocation()
  React.useEffect(() => {
    if (helpLocation.hash === '#compliance-process') complianceRef.current?.scrollIntoView({ block: 'start' })
    if (helpLocation.hash === '#caf-help') cafKeyConceptsRef.current?.scrollIntoView({ block: 'start' })
  }, [helpLocation.hash])
  const cafKeyConceptsRef = React.useRef(null)
  const cafNewRef = React.useRef(null)
  const cafProposalsRef = React.useRef(null)
  const cafSubmitRef = React.useRef(null)
  const cafApprovalsRef = React.useRef(null)
  const cafTasksRef = React.useRef(null)
  const cafCompletionRef = React.useRef(null)
  const cafTroubleshootingRef = React.useRef(null)

  const pmOverviewRef = React.useRef(null)
  const pmNewRef = React.useRef(null)
  const pmScheduleRef = React.useRef(null)
  const pmEntrataRef = React.useRef(null)
  const pmTimelineRef = React.useRef(null)
  const pmTroubleshootingRef = React.useRef(null)

  const searchInputRef = React.useRef(null)
  const trainingVideoRef = React.useRef(null)
  const trainingCloseRef = React.useRef(null)
  const clipVideoRef = React.useRef(null)
  const clipCloseRef = React.useRef(null)

  // Index of sections for search
  const HELP_SECTIONS = React.useMemo(
    () => [
      { id: 'compliance', label: 'Compliance Process', ref: complianceRef, keywords: ['compliance', 'certifications', 'requirements', 'appeal counter', 'appeal window', 'days remaining', 'high-rise', 'certification notifications', 'expiring certifications', 'current compliance', 'renewal readiness', 'crs', 'certificate', 'rental suitability', 'atlas', 'eclipse', 'violations', 'action plan', 'license', 'renewal', 'counsel', 'posting', 'receipt', 'restatement', 'audit', 'activity history', 'failures', 'email history', 'counsel emails', 'reference', 'outlook', 'attachments', 'drag and drop', 'appeal', 'business days', '45 days', '14 days', '30 days', 'closeout', 'evidence', 'escalation', 'regional'] },
      {
        id: 'quickLinks',
        label: 'Quick Links',
        ref: quickLinksRef,
        keywords: ['help', 'support', 'caf', 'pm', 'tasks', 'capital approval', 'documents', 'community docs'],
      },
      {
        id: 'cafKeyConcepts',
        label: 'CAF Key Concepts',
        ref: cafKeyConceptsRef,
        keywords: ['caf', 'capital', 'approval', 'status', 'stage', 'tasks'],
      },
      {
        id: 'cafNew',
        label: 'Creating a New CAF',
        ref: cafNewRef,
        keywords: ['new caf', 'create caf', 'draft', 'contract', 'community'],
      },
      {
        id: 'cafProposals',
        label: 'CAF Proposals & Supporting Documents',
        ref: cafProposalsRef,
        keywords: ['proposals', 'files', 'images', 'documents', 'pdf', 'community documents', 'research'],
      },
      {
        id: 'cafSubmit',
        label: 'Submitting a CAF for Approval',
        ref: cafSubmitRef,
        keywords: ['submit', 'approval', 'awaiting approval', 'in_review'],
      },
      {
        id: 'cafApprovals',
        label: 'How CAF Approvals Work',
        ref: cafApprovalsRef,
        keywords: ['approver', 'approve', 'reject', 'send back', 'chain'],
      },
      {
        id: 'cafTasks',
        label: 'Using the “My Tasks” Page',
        ref: cafTasksRef,
        keywords: ['my tasks', 'drafts', 'awaiting my approval', 'presets'],
      },
      {
        id: 'cafCompletion',
        label: 'CAF Project Completion',
        ref: cafCompletionRef,
        keywords: ['completion', 'actual amount', 'budgeted', 'complete'],
      },
      {
        id: 'cafTroubleshooting',
        label: 'CAF Troubleshooting & FAQ',
        ref: cafTroubleshootingRef,
        keywords: ['missing caf', 'cannot approve', 'approval limits', 'missing documents', 'community documents'],
      },
      {
        id: 'pmOverview',
        label: 'Preventive Maintenance Overview',
        ref: pmOverviewRef,
        keywords: ['pm', 'preventive maintenance', 'templates', 'schedule'],
      },
      {
        id: 'pmNew',
        label: 'Creating a New PM Template',
        ref: pmNewRef,
        keywords: ['new pm', 'pm template', 'pm basics', 'community', 'facility type'],
      },
      {
        id: 'pmSchedule',
        label: 'PM Scheduling & “Next” Run',
        ref: pmScheduleRef,
        keywords: ['schedule', 'frequency', 'interval', 'next', 'lead time'],
      },
      {
        id: 'pmEntrata',
        label: 'PM Work Orders & Entrata Mapping',
        ref: pmEntrataRef,
        keywords: ['entrata', 'work orders', 'property id', 'mapping'],
      },
      {
        id: 'pmTimeline',
        label: 'PM Timeline & Audit History',
        ref: pmTimelineRef,
        keywords: ['timeline', 'history', 'audit', 'changes'],
      },
      {
        id: 'pmTroubleshooting',
        label: 'PM Troubleshooting & FAQ',
        ref: pmTroubleshootingRef,
        keywords: ['pm missing', 'no permission', 'next not showing'],
      },
    ],
    []
  )

  function handleSearch(e) {
    e.preventDefault()
    const q = searchTerm.trim().toLowerCase()
    if (!q) return

    const match = HELP_SECTIONS.find(sec => {
      const haystack =
        (sec.label + ' ' + (sec.keywords || []).join(' ')).toLowerCase()
      return haystack.includes(q)
    })

    if (match && match.ref && match.ref.current) {
      match.ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
      setLastMatch(match.label)
    } else {
      setLastMatch('')
      // Optionally you could show a toast or inline “no results” message
    }
  }

  const openTrainingVideo = React.useCallback((e) => {
    if (e) {
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button === 1) {
        return
      }
      e.preventDefault()
    }
    setTrainingOpen(true)
  }, [])

  const closeTrainingVideo = React.useCallback(() => {
    if (trainingVideoRef.current) {
      trainingVideoRef.current.pause()
      trainingVideoRef.current.currentTime = 0
    }
    setTrainingOpen(false)
  }, [])

  const openClip = React.useCallback((config) => {
    setClipConfig(config)
    setClipOpen(true)
  }, [])

  const closeClip = React.useCallback(() => {
    if (clipVideoRef.current) {
      clipVideoRef.current.pause()
      clipVideoRef.current.currentTime = 0
    }
    setClipOpen(false)
  }, [])

  // Press "/" to focus the search bar
  React.useEffect(() => {
    function onKeyDown(e) {
      if (e.key === '/' && !e.ctrlKey && !e.metaKey && !e.altKey) {
        const tag = (e.target && e.target.tagName) || ''
        if (tag === 'INPUT' || tag === 'TEXTAREA') return
        e.preventDefault()
        if (searchInputRef.current) {
          searchInputRef.current.focus()
        }
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [])

  React.useEffect(() => {
    if (!trainingOpen) return
    const onKey = (e) => {
      if (e.key === 'Escape') closeTrainingVideo()
    }
    window.addEventListener('keydown', onKey)
    const t = setTimeout(() => trainingCloseRef.current?.focus(), 0)
    const playTimer = setTimeout(() => {
      const el = trainingVideoRef.current
      if (el) {
        const result = el.play()
        if (result && typeof result.catch === 'function') {
          result.catch(() => {})
        }
      }
    }, 0)
    return () => {
      window.removeEventListener('keydown', onKey)
      clearTimeout(t)
      clearTimeout(playTimer)
    }
  }, [trainingOpen, closeTrainingVideo])

  React.useEffect(() => {
    if (!clipOpen) return
    const onKey = (e) => {
      if (e.key === 'Escape') closeClip()
    }
    window.addEventListener('keydown', onKey)
    const t = setTimeout(() => clipCloseRef.current?.focus(), 0)
    return () => {
      window.removeEventListener('keydown', onKey)
      clearTimeout(t)
    }
  }, [clipOpen, closeClip])

  React.useEffect(() => {
    if (!clipOpen || !clipConfig) return
    const el = clipVideoRef.current
    if (!el) return

    const start = Number(clipConfig.start || 0)
    const end = Number(clipConfig.end || 0)

    const ensureStart = () => {
      el.currentTime = start
      const result = el.play()
      if (result && typeof result.catch === 'function') {
        result.catch(() => {})
      }
    }

    const onTimeUpdate = () => {
      if (end > 0 && el.currentTime >= end) {
        el.pause()
        el.currentTime = start
      }
    }

    if (el.readyState >= 1) ensureStart()
    else el.addEventListener('loadedmetadata', ensureStart, { once: true })

    el.addEventListener('timeupdate', onTimeUpdate)
    return () => {
      el.removeEventListener('timeupdate', onTimeUpdate)
    }
  }, [clipOpen, clipConfig])

  return (
    <div>
      <h2
  style={{
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginTop: 14,
    marginBottom: 12,
  }}
>
  <span>Help &amp; Support</span>
</h2>

      {/* Quick links + search */}
      <div ref={quickLinksRef} className="card">
        <p>
          Welcome! Use this page as a quick reference for how Capital Approval
          Forms (CAFs), Preventive Maintenance (PM), Compliance, tasks, and supporting
          documents work in this app.
        </p>

        <p className="muted">
          CAF proposals, files, and images are also available from the{' '}
          <strong>Community Documents</strong> page so that users with the
          appropriate viewer access can research work across communities.
        </p>

        {/* Search within help */}
        <form onSubmit={handleSearch} style={{ marginBottom: 12 }}>
          <label>
            Search Help
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search help (e.g. “CRS”, “License”, “New PM”, “Tasks”)"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </label>
          <div
            className="muted"
            style={{ fontSize: 12, marginTop: 4, display: 'flex', gap: 12 }}
          >
            <span>Press <kbd>/</kbd> to jump to this search.</span>
            {lastMatch && (
              <span>
                Jumped to: <strong>{lastMatch}</strong>
              </span>
            )}
          </div>
        </form>

        <ul>
          {/* <li>
            <Link to="/dev/caf">Capital Approval Forms</Link> (CAF list)
          </li>
          <li>
            <Link to="/pm">Preventive Maintenance</Link> (PM templates)
          </li>
          <li>
            <Link to="/tasks">My Tasks</Link> (approvals &amp; work items)
          </li> */}
          <li>
            <a href="#compliance-process" onClick={e=>{e.preventDefault();complianceRef.current?.scrollIntoView({behavior:'smooth',block:'start'})}}>Compliance process</a>
          </li>
          <li>
            <a href="#caf-help" onClick={e=>{e.preventDefault();cafKeyConceptsRef.current?.scrollIntoView({behavior:'smooth',block:'start'})}}>CAF help</a>
          </li>
          <li>
            <a href={trainingVideoUrl} onClick={openTrainingVideo}>
              Training Video
            </a>
          </li>
          <li>
            <a href="mailto:nhammerschlag@scullycompany.com">
              Email Support (Neal Hammerschlag)
            </a>
          </li>
          <li>
            <a href="mailto:vtomasso@scullycompany.com">
              Email Support (Valerie Tomasso)
            </a>
          </li>
        </ul>
      </div>

      {trainingOpen && (
        <div
          className="modal-backdrop"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeTrainingVideo()
          }}
        >
          <div
            className="modal-card card"
            role="dialog"
            aria-modal="true"
            aria-labelledby="training-video-title"
            style={{ width: 'min(960px, calc(100% - 32px))' }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
              }}
            >
              <h3 id="training-video-title" style={{ margin: 0 }}>
                MaintainIQ Training Video
              </h3>
              <button ref={trainingCloseRef} onClick={closeTrainingVideo}>
                Close
              </button>
            </div>

            <div style={{ marginTop: 12 }}>
              <video
                ref={trainingVideoRef}
                src={trainingVideoUrl}
                controls
                playsInline
                style={{
                  width: '100%',
                  maxHeight: '70vh',
                  borderRadius: 8,
                  background: '#000',
                }}
              />
            </div>
          </div>
        </div>
      )}

      {clipOpen && (
        <div
          className="modal-backdrop"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeClip()
          }}
        >
          <div
            className="modal-card card"
            role="dialog"
            aria-modal="true"
            aria-labelledby="help-clip-title"
            style={{
              width: 'min(520px, calc(100% - 32px))',
              position: 'fixed',
              top: 96,
              right: 96,
              margin: 0,
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
              }}
            >
              <h3 id="help-clip-title" style={{ margin: 0 }}>
                {clipConfig?.title || 'Help Clip'}
              </h3>
              <button ref={clipCloseRef} onClick={closeClip}>
                Close
              </button>
            </div>

            <div style={{ marginTop: 12 }}>
              <video
                ref={clipVideoRef}
                src={trainingVideoUrl}
                controls
                playsInline
                style={{
                  width: '100%',
                  maxHeight: '50vh',
                  borderRadius: 8,
                  background: '#000',
                }}
              />
            </div>
          </div>
        </div>
      )}

      <div ref={complianceRef}><ComplianceHelp /></div>

      {/* CAF: Key concepts */}
      <div id="caf-help" ref={cafKeyConceptsRef} className="card">
        <div className="help-card-header">
          <h3>Key Concepts (CAF)</h3>
          <button
            type="button"
            className="help-clip-btn"
            aria-label="Play Key Concepts help clip"
            onClick={() =>
              openClip({
                title: 'Key Concepts (CAF)',
                start: 66,
                end: 156,
              })
            }
          >
            ?
          </button>
        </div>
        <ul>
          <li>
            <strong>CAF (Capital Approval Form)</strong> – the main record that
            tracks a capital spending request (community, title, contract price,
            proposals, files, and approvals).
          </li>
          <li>
            <strong>Status</strong> – where the CAF is in its life cycle:
            <code>draft</code>, <code>awaiting_approval</code>,{' '}
            <code>in_review</code>, <code>changes_requested</code>,{' '}
            <code>approved</code>, <code>complete</code>.
          </li>
          <li>
            <strong>Stage</strong> – which role is currently reviewing the CAF
            (e.g. <em>Manager</em>, <em>Regional Manager</em>,{' '}
            <em>VP / Facilities</em>, <em>VP / Operations</em>).
          </li>
          <li>
            <strong>Approver Chain</strong> – the ordered list of roles that
            must approve based on the amount (Manager → Regional Manager → VP /
            Facilities → VP / Operations, etc.).
          </li>
          <li>
            <strong>Tasks</strong> – work items that appear on the{' '}
            <em>My Tasks</em> page. A CAF generates tasks for the drafter and
            for each approver as the CAF moves through the chain.
          </li>
        </ul>
      </div>

      {/* CAF: Getting started / creating CAFs */}
      <div ref={cafNewRef} className="card">
        <div className="help-card-header">
          <h3>Creating a New CAF</h3>
          <button
            type="button"
            className="help-clip-btn"
            aria-label="Play Creating a New CAF help clip"
            onClick={() =>
              openClip({
                title: 'Creating a New CAF',
                start: 155,
                end: 199,
              })
            }
          >
            ?
          </button>
        </div>
        <ol>
          <li>
            Go to <Link to="/dev/caf">Capital Approval Forms</Link>.
          </li>
          <li>
            Click <strong>+ New CAF</strong> in the top-right corner.
          </li>
          <li>
            Fill in the required information:
            <ul>
              <li>
                <strong>Community</strong>
              </li>
              <li>
                <strong>Title</strong> and <strong>Facility Type</strong>
              </li>
              <li>
                <strong>Contract Price</strong>
              </li>
              <li>Any initial notes that help explain the request</li>
            </ul>
          </li>
          <li>
            As you work, the CAF stays in <code>draft</code> status and a
            “Draft CAF” task is assigned to you on the <em>My Tasks</em> page.
          </li>
        </ol>
        <p className="muted">
          You can leave a draft and come back later from{' '}
          <Link to="/dev/caf">Capital Approval Forms</Link> or from{' '}
          <Link to="/tasks">My Tasks</Link> → <em>My Drafts</em>.
        </p>
      </div>

      {/* CAF: Proposals & files */}
      <div ref={cafProposalsRef} className="card">
        <div className="help-card-header">
          <h3>Proposals &amp; Supporting Documents</h3>
          <button
            type="button"
            className="help-clip-btn"
            aria-label="Play Proposals and Supporting Documents help clip"
            onClick={() =>
              openClip({
                title: 'Proposals & Supporting Documents',
                start: 199,
                end: 293,
              })
            }
          >
            ?
          </button>
        </div>
        <h4>Proposals</h4>
        <ul>
          <li>
            Use the <strong>Proposals</strong> section in the CAF to upload PDF
            proposals from vendors (Vendor, Price, Notes, and a PDF file are
            required).
          </li>
          <li>
            The yellow pill shows how many proposals are still required (e.g.
            “Needs 3 more”).
          </li>
          <li>
            Mark one proposal as <strong>Recommended</strong> once you have a
            preferred vendor.
          </li>
          <li>
            Proposals are stored with the CAF and are also available on the{' '}
            <strong>Community Documents</strong> page as document type{' '}
            <strong>Proposal</strong>, so they can be searched by CAF number,
            title, community, or tags.
          </li>
        </ul>
        <h4>Files &amp; Images</h4>
        <ul>
          <li>
            Use the <strong>Files &amp; Images</strong> section to upload
            additional documents (images, signed proposals, invoices, completion
            photos, etc.).
          </li>
          <li>
            Choose a <strong>Document Type</strong> so documents can be found
            from other parts of the system (for example, when searching from the{' '}
            <strong>Community Documents</strong> page).
          </li>
          <li>
            Like proposals, CAF files and images are surfaced in{' '}
            <strong>Community Documents</strong> and can be filtered by type,
            CAF number, community, or tags for research.
          </li>
        </ul>
        <p className="muted">
          Anyone with the appropriate <strong>viewer</strong> access can see CAF
          documents (proposals, files, images) in the Community Documents list,
          even if those CAFs belong to communities they don’t normally work
          with. Approvals and CAF editing are still controlled by your role and
          community permissions.
        </p>
      </div>

      {/* CAF: Submit & approval flow */}
      <div ref={cafSubmitRef} className="card">
        <div className="help-card-header">
          <h3>Submitting a CAF for Approval</h3>
          <button
            type="button"
            className="help-clip-btn"
            aria-label="Play Submitting a CAF for Approval help clip"
            onClick={() =>
              openClip({
                title: 'Submitting a CAF for Approval',
                start: 294,
                end: 343,
              })
            }
          >
            ?
          </button>
        </div>
        <ol>
          <li>
            Open your CAF from the{' '}
            <Link to="/dev/caf">Capital Approval Forms</Link> list.
          </li>
          <li>
            Complete required fields, add proposals, and upload any supporting
            files.
          </li>
          <li>
            Optionally enter a comment in the <strong>Optional comment</strong>{' '}
            field at the top – this will be logged on the timeline.
          </li>
          <li>
            Click <strong>Submit</strong>.
          </li>
        </ol>
        <p>After you submit:</p>
        <ul>
          <li>
            The CAF status becomes <code>awaiting_approval</code> or{' '}
            <code>in_review</code>.
          </li>
          <li>
            The <strong>Stage</strong> is set to the first approver in the
            chain (usually <em>Manager</em>).
          </li>
          <li>
            An <strong>approval task</strong> is created for the approver’s role
            and appears on their <em>My Tasks</em> page under{' '}
            <strong>Awaiting My Approval</strong>.
          </li>
        </ul>
      </div>

      {/* CAF: How approvals work */}
      <div ref={cafApprovalsRef} className="card">
        <div className="help-card-header">
          <h3>How Approvals Work (CAF)</h3>
          <button
            type="button"
            className="help-clip-btn"
            aria-label="Play How Approvals Work (CAF) help clip"
            onClick={() =>
              openClip({
                title: 'How Approvals Work (CAF)',
                start: 343,
                end: 415,
              })
            }
          >
            ?
          </button>
        </div>

        <h4>For Approvers</h4>
        <ol>
          <li>
            Go to <Link to="/tasks">My Tasks</Link> and choose the{' '}
            <strong>Awaiting My Approval</strong> preset.
          </li>
          <li>
            Click the CAF number in the <strong>Related</strong> column to open
            the CAF.
          </li>
          <li>
            Review <strong>Overview</strong>, <strong>Proposals</strong>,{' '}
            <strong>Files &amp; Images</strong>, and the{' '}
            <strong>Timeline</strong>.
          </li>
          <li>Optionally add a short comment at the top.</li>
          <li>
            Click <strong>Approve</strong> to move the CAF to the next stage, or
            click <strong>Reject / Send Back</strong> if changes are required.
          </li>
        </ol>

        <h4>Approve</h4>
        <ul>
          <li>
            If you are <strong>not</strong> the final approver, the CAF status
            moves to <code>in_review</code> and the <strong>Stage</strong>{' '}
            advances to the next role in the approver chain. A new approval task
            is created for that role.
          </li>
          <li>
            If you are the <strong>final approver</strong> for the amount, the
            CAF status becomes <code>approved</code>. A task is left in{' '}
            <strong>Approved / Completion</strong> until project completion is
            entered.
          </li>
        </ul>

        <h4>Reject / Send Back</h4>
        <ul>
          <li>
            The CAF status becomes <code>changes_requested</code>.
          </li>
          <li>
            The Stage resets to the first approver (Manager) so the originator
            can make updates.
          </li>
          <li>
            A <strong>“Needs My Changes”</strong> draft task is assigned back to
            the CAF creator.
          </li>
        </ul>
      </div>

      {/* CAF: My Tasks explanation */}
      <div ref={cafTasksRef} className="card">
        <div className="help-card-header">
          <h3>Using the “My Tasks” Page</h3>
          <button
            type="button"
            className="help-clip-btn"
            aria-label="Play Using the My Tasks Page help clip"
            onClick={() =>
              openClip({
                title: 'Using the “My Tasks” Page',
                start: 415,
                end: 482,
              })
            }
          >
            ?
          </button>
        </div>
        <p>
          The <strong>My Tasks</strong> page shows all items related to you
          (drafts, approvals, and other work items). Use the presets at the top
          to filter:
        </p>
        <ul>
          <li>
            <strong>All</strong> – everything you can see.
          </li>
          <li>
            <strong>Assigned to Me</strong> – items directly assigned to your
            email.
          </li>
          <li>
            <strong>Awaiting My Approval</strong> – CAFs where your role is the
            current approver.
          </li>
          <li>
            <strong>Needs My Changes</strong> – CAFs sent back to you for
            revision.
          </li>
          <li>
            <strong>My Drafts</strong> – CAF drafts you created.
          </li>
          <li>
            <strong>Submitted</strong>, <strong>In Review</strong>, and{' '}
            <strong>Approved / Completion</strong> – helpful for tracking CAFs
            you started as they move through the chain.
          </li>
        </ul>
        <p>
          Click the <strong>CAF #</strong> in the <em>Related</em> column to
          jump directly into that CAF’s detail page.
        </p>
      </div>

      {/* CAF: Project completion */}
      <div ref={cafCompletionRef} className="card">
        <div className="help-card-header">
          <h3>Project Completion (CAF)</h3>
          <button
            type="button"
            className="help-clip-btn"
            aria-label="Play Project Completion (CAF) help clip"
            onClick={() =>
              openClip({
                title: 'Project Completion (CAF)',
                start: 482,
                end: 500,
              })
            }
          >
            ?
          </button>
        </div>
        <ul>
          <li>
            Once the project is done, the requester (or another responsible
            user) enters <strong>Budgeted Amount</strong> and{' '}
            <strong>Actual Amount</strong> in the <em>Project Completion</em>{' '}
            panel on the CAF.
          </li>
          <li>
            Saving project completion moves the CAF toward <code>complete</code>{' '}
            and updates the remaining “Approved / Completion” tasks.
          </li>
        </ul>
      </div>

      {/* CAF: Troubleshooting */}
      <div ref={cafTroubleshootingRef} className="card">
        <h3>Troubleshooting &amp; FAQ (CAF)</h3>
        <ul>
          <li>
            <strong>I don’t see a CAF I expect.</strong> Check your{' '}
            <em>Community</em> filter on the CAF list and your{' '}
            <em>My Tasks</em> presets. CAF records and approvals are still
            scoped to your assigned communities and roles.
          </li>
          <li>
            <strong>I don’t see a proposal or file I expect.</strong> Go to the{' '}
            <strong>Community Documents</strong> page and:
            <ul>
              <li>Set <strong>Type</strong> to the document type (e.g. Proposal, Images).</li>
              <li>
                Use the search box to look for the <strong>CAF number</strong>,
                title, community, or tags.
              </li>
              <li>
                If available, choose <strong>All</strong> in the Community
                filter to see documents across communities.
              </li>
            </ul>
            Users with a viewer role can research CAF documents across all
            communities, even if they are not assigned to those communities for
            approvals.
          </li>
          <li>
            <strong>I can’t approve a CAF.</strong> Make sure your job title /
            role matches the current <strong>Stage</strong> in the Approver
            Chain. If it doesn’t, contact an admin to correct your user profile
            or approval limits.
          </li>
          <li>
            <strong>The app says my approval limits are not configured.</strong>{' '}
            This means there is no approval limit record for that amount. Email
            an admin so they can update the approval limits.
          </li>
          <li>
            <strong>I’m not sure what to do next.</strong> Check the CAF’s{' '}
            <strong>Timeline</strong> and <strong>Messages</strong> for
            comments, or reach out to support.
          </li>
        </ul>
      </div>

      {/* -------------------- PM HELP CONTENT -------------------- */}

      {/* PM: Overview */}
      <div ref={pmOverviewRef} className="card">
        <div className="help-card-header">
          <h3>Preventive Maintenance Overview</h3>
          <button
            type="button"
            className="help-clip-btn"
            aria-label="Play Preventive Maintenance Overview help clip"
            onClick={() =>
              openClip({
                title: 'Preventive Maintenance Overview',
                start: 599,
                end: 665,
              })
            }
          >
            ?
          </button>
        </div>
        <ul>
          <li>
            <strong>PM Template</strong> – a reusable schedule that generates
            preventive maintenance work (and work orders) for one or more
            communities.
          </li>
          <li>
            <strong>Community</strong> – PMs are scoped to communities, just
            like CAFs. You only see PMs for communities tied to your user.
          </li>
          <li>
            <strong>Facility Type</strong> – the general category of asset or
            area the PM covers (e.g. roofs, HVAC, grounds).
          </li>
          <li>
            <strong>Schedule</strong> – the cadence for generating PM work
            (frequency, interval, start date, time of day, lead time).
          </li>
          <li>
            <strong>Next</strong> – on the PM list, this column shows the next
            time the PM is expected to run based on the schedule.
          </li>
          <li>
            <strong>Active</strong> – when checked, the PM template continues to
            generate future PM work and work orders. When unchecked, history is
            preserved but no new work will be generated.
          </li>
        </ul>
      </div>

      {/* PM: Creating a new PM */}
      <div ref={pmNewRef} className="card">
        <div className="help-card-header">
          <h3>Creating a New PM Template</h3>
          <button
            type="button"
            className="help-clip-btn"
            aria-label="Play Creating a New PM Template help clip"
            onClick={() =>
              openClip({
                title: 'Creating a New PM Template',
                start: 665,
                end: 735,
              })
            }
          >
            ?
          </button>
        </div>
        <ol>
          <li>
            Go to <Link to="/pm">Preventive Maintenance</Link>.
          </li>
          <li>
            Click <strong>+ New PM</strong> in the top-right corner.
          </li>
          <li>
            In the <strong>PM Basics</strong> card:
            <ul>
              <li>
                Enter a <strong>Title</strong> that clearly describes the work
                (e.g. “Monthly Roof Inspection”).
              </li>
              <li>
                Choose a <strong>Facility Type</strong> (optional but helpful
                for reporting).
              </li>
              <li>
                Select one or more <strong>Communities</strong>. Hold Ctrl/Cmd
                (or Shift) to select multiple communities.
              </li>
              <li>
                Optionally add a detailed <strong>Description</strong> with
                instructions for the technician.
              </li>
            </ul>
          </li>
          <li>
            Complete the <strong>Schedule</strong> card (see below).
          </li>
          <li>
            If needed, expand <strong>Work Order Mapping (Entrata)</strong> to
            configure how work orders are created in Entrata.
          </li>
          <li>
            Click <strong>Create</strong>.
          </li>
        </ol>
        <p className="muted">
          When you create a PM for multiple communities, the system creates a
          separate PM template for each selected community using the same basic
          configuration.
        </p>
      </div>

      {/* PM: Scheduling & Next */}
      <div ref={pmScheduleRef} className="card">
        <div className="help-card-header">
          <h3>PM Scheduling &amp; “Next” Run</h3>
          <button
            type="button"
            className="help-clip-btn"
            aria-label="Play PM Scheduling and Next Run help clip"
            onClick={() =>
              openClip({
                title: 'PM Scheduling & “Next” Run',
                start: 735,
                end: 891,
              })
            }
          >
            ?
          </button>
        </div>
        <p>
          The <strong>Schedule</strong> card controls how often the PM runs and
          when the next work order will be created.
        </p>
        <ul>
          <li>
            <strong>Frequency</strong> – how often the PM repeats (weekly,
            monthly, quarterly, yearly).
          </li>
          <li>
            <strong>Interval</strong> – works with Frequency. For example:
            <ul>
              <li>
                Frequency = <code>monthly</code>, Interval = <code>1</code> →
                every month.
              </li>
              <li>
                Frequency = <code>monthly</code>, Interval = <code>3</code> →
                every 3 months.
              </li>
              <li>
                Frequency = <code>weekly</code>, Interval = <code>2</code> →
                every 2 weeks.
              </li>
            </ul>
          </li>
          <li>
            <strong>Start Date</strong> – the first time the PM is scheduled to
            occur.
          </li>
          <li>
            <strong>Time of Day</strong> – when the PM work is expected to
            happen (used when creating work orders).
          </li>
          <li>
            <strong>Estimated Minutes</strong> – how long the PM usually takes;
            helps with planning and dispatch.
          </li>
          <li>
            <strong>Work order lead time (days)</strong> – how many days before
            the PM date the work order should be created. For example, if the PM
            is scheduled for the 15th and lead time is 3 days, the work order
            will be created on the 12th.
          </li>
          <li>
            <strong>End Date (optional)</strong> – stop generating new PM work
            after this date.
          </li>
          <li>
            <strong>Active</strong> – when unchecked, the PM template will not
            generate future PM work or work orders, but its history remains.
          </li>
        </ul>
        <p>
          While editing the schedule, the system shows four helpful summaries:
        </p>
        <ul>
          <li>
            <strong>First PM occurrence</strong> – based on Start Date and Time
            of Day.
          </li>
          <li>
            <strong>First work order will be created</strong> – first PM date
            minus the lead time (in days).
          </li>
          <li>
            <strong>Next PM occurrence (from today)</strong> – the next time
            this PM will run according to the schedule, starting from now.
          </li>
          <li>
            <strong>Next work order will be created</strong> – when the next
            work order will be generated before that next PM.
          </li>
        </ul>
        <p className="muted">
          On the <Link to="/pm">Preventive Maintenance</Link> list, the{' '}
          <strong>Next</strong> column is calculated from this same schedule
          information.
        </p>
      </div>

      {/* PM: Entrata mapping */}
      <div ref={pmEntrataRef} className="card">
        <div className="help-card-header">
          <h3>PM Work Orders &amp; Entrata Mapping</h3>
          <button
            type="button"
            className="help-clip-btn"
            aria-label="Play PM Work Orders and Entrata Mapping help clip"
            onClick={() =>
              openClip({
                title: 'PM Work Orders & Entrata Mapping',
                start: 891,
                end: 957,
              })
            }
          >
            ?
          </button>
        </div>
        <p>
          The <strong>Work Order Mapping (Entrata)</strong> card controls how
          the PM maps to Entrata when creating work orders.
        </p>
        <ul>
          <li>
            <strong>Property ID (Entrata)</strong> – defaults from the selected
            community’s <code>entrataCommunityId</code> if available. You can
            override this if needed.
          </li>
          <li>
            <strong>Maintenance Location / Category / Problem IDs</strong> –
            map PM work to the correct Entrata maintenance codes.
          </li>
          <li>
            <strong>Assigned To</strong> – optional Entrata user id for
            assigning the work order by default.
          </li>
          <li>
            <strong>Priority ID / Priority Order #</strong> – control how
            important this PM work order is in Entrata.
          </li>
          <li>
            <strong>Permission To Enter</strong> – whether the technician has
            permission to enter by default.
          </li>
          <li>
            <strong>Floating Work Order</strong> – whether the work order is
            attached to a specific lease/unit.
          </li>
        </ul>
        <p className="muted">
          This section is primarily for admins / system configuration. As Entrata
          API integration improves, more of this mapping can be automated or
          hidden from everyday users.
        </p>
      </div>

      {/* PM: Timeline */}
      <div ref={pmTimelineRef} className="card">
        <div className="help-card-header">
          <h3>PM Timeline &amp; Audit History</h3>
          <button
            type="button"
            className="help-clip-btn"
            aria-label="Play PM Timeline and Audit History help clip"
            onClick={() =>
              openClip({
                title: 'PM Timeline & Audit History',
                start: 957,
                end: 1000,
              })
            }
          >
            ?
          </button>
        </div>
        <p>
          Each PM template has a <strong>Timeline</strong> that tracks key
          events, similar to CAFs.
        </p>
        <ul>
          <li>
            When a PM is created, a “PM template created” entry is added with the
            user and timestamp.
          </li>
          <li>
            Future edits (such as schedule changes or deactivating a PM) can be
            added to the timeline so you can see who changed what and when.
          </li>
          <li>
            This is helpful when troubleshooting why the <strong>Next</strong>{' '}
            column changed or why work orders started/stopped being created.
          </li>
        </ul>
      </div>

      {/* PM: Troubleshooting */}
      <div ref={pmTroubleshootingRef} className="card">
        <h3>Troubleshooting &amp; FAQ (PM)</h3>
        <ul>
          <li>
            <strong>I don’t see a PM I expect.</strong> Check the{' '}
            <em>Community</em> filter on the PM list and confirm that your user
            is assigned to that community. PM templates are scoped by
            community, similar to CAFs.
          </li>
          <li>
            <strong>The “Next” column is blank.</strong> Make sure the PM has a
            valid <strong>Start Date</strong>, <strong>Time of Day</strong>, and{' '}
            <strong>Frequency</strong>. If those are missing or invalid, the
            system can’t calculate a next occurrence.
          </li>
          <li>
            <strong>I can’t create or edit a PM.</strong> You need permissions
            for the target community, or an admin/system-config role. If you
            think you should have access, contact an admin to review your user
            record and communities.
          </li>
          <li>
            <strong>Work orders are not showing up when I expect.</strong> Check
            the <strong>work order lead time</strong>, <strong>Active</strong>{' '}
            flag, and <strong>End Date</strong>. Also confirm the Entrata
            mapping (Property / Location / Category IDs).
          </li>
        </ul>
      </div>

    </div>
  )
}
